import * as React from 'react';
import Alert from '@narvar/nth-block-alert';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import InitialPanel from './InitialPanel';
import CustomComponentContext from '../CustomComponentContext';
import {
  useRedirectToHoldContext,
  UI_STATE,
} from '../../hooks/useRedirectToHoldState';
import SelectLocationPanel from './SelectLocationPanel';
import VerifyAddressPanel from './VerifyAddressPanel';
import ConfirmLocationPanel from './ConfirmLocationPanel';
import SuccessPanel from './SuccessPanel';
import PickupLocationTemplate from '../PickupPointDetails/components/PickupLocationTemplate';
import { RedirectToHoldStatus } from '../../../server/api/schema';

interface RthProps {
  rthStatus: RedirectToHoldStatus;
  featureName: string;
}

const RedirectToHold: React.FunctionComponent<RthProps> = ({
  rthStatus,
  featureName,
}: RthProps) => {
  const { state, signalContentLoaded, track } = React.useContext(
    CustomComponentContext,
  );

  const { localeLanguage, localeCountry, trackingStatusCode } =
    state?.pageContext || {};

  const { state: rthState, update: updateRthState } =
    useRedirectToHoldContext();

  const handleDismiss = () => {
    track({
      eventType: 'click',
      clickSubType: 'dismiss',
    });
    updateRthState({ ...rthStatus, uiState: UI_STATE.HIDDEN });
  };

  React.useEffect(() => {
    // `package_identifier` is required for subsequent API calls. If not
    // given, then hide the component.
    if (!rthStatus.packageIdentifier) {
      updateRthState({ uiState: UI_STATE.HIDDEN });
      return;
    }

    if (rthStatus.eligible && !rthStatus.scheduled) {
      updateRthState({
        uiState: UI_STATE.INITIAL,
        uniqueIdentifier: rthStatus.packageIdentifier,
      });
      return;
    }

    if (rthStatus.scheduled) {
      if (!rthStatus.storeLocation) {
        // eslint-disable-next-line no-console
        console.error('Redirect to hold store details are not defined');
      }

      updateRthState({
        uiState: UI_STATE.POST_SUCCESS,
        uniqueIdentifier: rthStatus.packageIdentifier,
        selectedLocation: rthStatus.storeLocation ?? undefined,
      });
      return;
    }

    updateRthState({ uiState: UI_STATE.HIDDEN });
  }, [rthStatus]);

  const { panelcontainerNoneNone: panelcontainerStyle } = useTheme();

  const { marginBottom } = panelcontainerStyle || {};

  if (trackingStatusCode) {
    if (
      !['SHPD', 'INTR', 'EXCP', 'DLPP', 'ITPP'].includes(trackingStatusCode)
    ) {
      return null;
    }
  } else {
    // If no tracking status code is provided then we err on the side of letting
    // the /status API call inform this component on whether it should render
    // eslint-disable-next-line no-console
    console.error('No tracking status code provided');
  }

  // TODO: remove locale check when we figure out a good way
  // to do locale-based features across multiple retailers
  if (localeLanguage !== 'en' || localeCountry !== 'US') {
    // eslint-disable-next-line no-console
    console.error('Redirect to Hold: Unsupported locale');
    // eslint-disable-next-line no-console
    console.table({ localeLanguage, localeCountry });
    return null;
  }

  let panel = null;
  switch (rthState.uiState) {
    case UI_STATE.INITIAL:
      panel = <InitialPanel featureName={featureName} />;
      break;
    case UI_STATE.VERIFY_ADDRESS:
      panel = <VerifyAddressPanel countryCode={localeCountry} />;
      break;
    case UI_STATE.SELECT_LOCATION:
      panel = <SelectLocationPanel featureName={featureName} />;
      break;
    case UI_STATE.CONFIRM_LOCATION:
      panel = <ConfirmLocationPanel featureName={featureName} />;
      break;
    case UI_STATE.SUCCESS:
      panel = <SuccessPanel featureName={featureName} />;
      break;
    case UI_STATE.POST_SUCCESS:
      panel = (
        <PickupLocationTemplate
          featureName={featureName}
          storeLocation={rthStatus.storeLocation}
        />
      );
      break;
    case UI_STATE.HIDDEN:
      panel = null;
      break;
    default:
      ((x: never) => x)(rthState.uiState);
  }

  signalContentLoaded?.();

  const isDismissible = rthState.uiState !== UI_STATE.POST_SUCCESS;

  return (
    <div
      style={{
        paddingBottom: marginBottom || '0.5rem',
        // TODO: remove when component is in `nth`.
        // hide until useTheme returns an actual value to avoid flash of defaults
        display: panelcontainerStyle ? 'block' : 'none',
      }}
    >
      <Alert
        featureName={featureName}
        show={rthState.uiState !== UI_STATE.HIDDEN}
        dismissible={isDismissible}
        dismissOnClick={handleDismiss}
      >
        <div style={{ padding: '0.25rem' }}>{panel}</div>
      </Alert>
    </div>
  );
};

export default withThemeProvider(RedirectToHold, {
  themeTokens: ['panelcontainerNoneNone'],
});
